import React, { useEffect, useRef } from 'react';

import { getStepLabel, usePipelineEvents } from '../shared/hooks/use-pipeline-events';
import { useRotatingTip } from '../shared/hooks/use-rotating-tip';

// The demo org is small and sync normally finishes in a few seconds, but there's no
// guarantee the pipeline emits a run_completed event (or that we catch it) — don't
// strand the guest on this screen forever if that happens.
const FALLBACK_DELAY_MS = 20000;

interface Props {
  // True once POST /api/sandbox/complete resolved and the BE pipeline was actually kicked off.
  triggered: boolean;
  onReady: () => void;
}

// Mirrors OnboardingFlow's "syncing" step: reuses the same pipeline-events stream and
// rotating tips so guest sandbox setup and real Jira sync feel like the same mechanism.
const SandboxProvisioning: React.FC<Props> = ({ triggered, onReady }) => {
  const { runs } = usePipelineEvents();
  const { tip, tipVisible } = useRotatingTip(true);
  const firedRef = useRef(false);

  const fire = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    onReady();
  };

  useEffect(() => {
    if (runs.some((r) => r.status === 'completed')) fire();
  }, [runs]);

  useEffect(() => {
    if (!triggered) return undefined;
    const timeout = setTimeout(fire, FALLBACK_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [triggered]);

  const activeRun = runs.find((r) => r.status === 'running');
  const activeStepId = activeRun?.steps.find((s) => s.status === 'running')?.stepId;
  const statusLabel = activeStepId
    ? getStepLabel(activeStepId)
    : triggered
      ? 'Starting sync…'
      : 'Provisioning your demo project…';

  return (
    <div className="flex flex-col items-center text-center gap-6 py-10">
      <span className="loading loading-ring loading-lg text-primary" />
      <div className="space-y-1">
        <h2 className="text-2xl font-bold font-display">Setting up your sandbox…</h2>
        <p className="opacity-60 max-w-xs mx-auto">{statusLabel}</p>
      </div>

      <div
        className="rounded-xl bg-base-200 shadow-card p-5 space-y-1.5 max-w-xs text-left transition-opacity duration-300"
        style={{ opacity: tipVisible ? 1 : 0 }}
      >
        <p className="text-xs font-semibold uppercase tracking-widest opacity-40">Did you know?</p>
        <p className="font-semibold text-sm">{tip.title}</p>
        <p className="text-xs opacity-60 leading-relaxed">{tip.body}</p>
      </div>
    </div>
  );
};

export default SandboxProvisioning;
